import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  VisibilityOff as HiddenIcon,
} from '@mui/icons-material';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

const AttachmentList = ({ itemId, onUpdate, refreshKey }) => {
  const { user, effectiveRole } = useAuth();
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, attachmentId: null });

  const fetchAttachments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/items/${itemId}/attachments`);

      // Filter attachments based on user role and visibility
      let filteredAttachments = response.data.attachments;
      if (effectiveRole === 'homeowner') {
        filteredAttachments = filteredAttachments.filter(att => att.visible_to_homeowner);
      }

      setAttachments(filteredAttachments);
      setError('');
    } catch (error) {
      console.error('Fetch attachments error:', error);
      setError('Failed to load attachments');
    } finally {
      setLoading(false);
    }
  }, [itemId, effectiveRole]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments, refreshKey]);

  const handleDelete = (attachmentId) => {
    setConfirmDialog({ open: true, attachmentId });
  };

  const handleConfirmDelete = async () => {
    const { attachmentId } = confirmDialog;
    setConfirmDialog({ open: false, attachmentId: null });
    try {
      await axios.delete(`/api/attachments/${attachmentId}`);
      setAttachments(attachments.filter(att => att.id !== attachmentId));
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Delete attachment error:', error);
      setError(error.response?.data?.error || 'Failed to delete attachment');
    }
  };

  const handleDownload = async (attachment) => {
    try {
      // In a real implementation, you'd need to set up a download endpoint
      // For now, we'll open the file in a new window
      window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/${attachment.file_path}`, '_blank');
    } catch (error) {
      console.error('Download error:', error);
      setError('Failed to download file');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (fileType) => {
    return <FileIcon color="action" />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (attachments.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2, fontStyle: 'italic' }}>
        No attachments yet
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Attachments ({attachments.length})
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <List dense>
        {attachments.map((attachment) => (
          <ListItem
            key={attachment.id}
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              mb: 1,
              bgcolor: 'white',
            }}
          >
            <Box sx={{ mr: 2 }}>
              {getFileIcon(attachment.file_type)}
            </Box>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    {attachment.original_filename}
                  </Typography>
                  {effectiveRole === 'contractor' && !attachment.visible_to_homeowner && (
                    <Tooltip title="Hidden from homeowner">
                      <HiddenIcon fontSize="small" color="disabled" />
                    </Tooltip>
                  )}
                </Box>
              }
              secondary={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="caption">
                    {formatFileSize(attachment.file_size)}
                  </Typography>
                  <Typography variant="caption">•</Typography>
                  <Typography variant="caption">
                    {formatDate(attachment.created_at)}
                  </Typography>
                  <Typography variant="caption">•</Typography>
                  <Typography variant="caption">
                    by {attachment.first_name} {attachment.last_name}
                  </Typography>
                  <Chip
                    label={attachment.role}
                    size="small"
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                </Box>
              }
            />
            <ListItemSecondaryAction>
              <Tooltip title="Download">
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => handleDownload(attachment)}
                  sx={{ mr: 1 }}
                  aria-label={`Download ${attachment.file_name || 'file'}`}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {(user.id === attachment.user_id || effectiveRole === 'admin') && (
                <Tooltip title="Delete">
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => handleDelete(attachment.id)}
                    color="error"
                    aria-label={`Delete ${attachment.file_name || 'file'}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, attachmentId: null })}
      >
        <DialogTitle>Delete Attachment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this attachment? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, attachmentId: null })}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttachmentList;
